import {useContext, useEffect, useMemo, useReducer, useState} from 'react';
import {Link, Outlet, useLocation, useParams, useNavigate} from 'react-router-dom';

import {Typography, IconButton, Skeleton} from '@mui/material';
import {ArrowBackIosNew} from '@mui/icons-material';

import AppContext from '../../context/AppContext';
import {request, URLs} from '../../api';
import {GroupContestViewDataQueryResponse} from '../../api/responses/ContestViewResponseTypes';
import {Page} from '../../components';
import {stringInterval} from '../../utils/date-utils';
import {Role} from '../../types';
import {ModuleRoleCode} from '../../types/entities/Role';

export type ContestContextType = {
  contest: GroupContestViewDataQueryResponse;
  isOwner: boolean;
  groupContestId: number;
};
export const Contest = () => {
  const context = useContext(AppContext);
  const {groupContest: groupContestId} = useParams();

  const [isLoading, setIsLoading] = useState(true);

  const [contest, setGroupContest] = useState<GroupContestViewDataQueryResponse | null>(null);
  const [isPrivileged, setIsPrivileged] = useState(false);

  const [update, forceUpdate] = useReducer((x) => x + 1, 0);

  const location = useLocation();

  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    if (groupContestId && !isNaN(Number(groupContestId))) {
      request<{}, {}, GroupContestViewDataQueryResponse>(URLs.getGroupContest(Number(groupContestId)), {
        method: 'GET',
        successCallback(response) {
          setGroupContest(response);
          setIsPrivileged(
            context.user?.role === Role.ADMIN || context.user?.activeModule?.role === ModuleRoleCode.COURSE_ADMIN
          );
          setIsLoading(false);
        },
        errorCallback(err: any) {
          if (err) {
            if (err.response && err.response.data && err.response.data.error) {
              if (err.response.data.error == 'MOVE_TO_OTHER_MODULE') {
                const redirectURL = `/contest/${groupContestId}/problems`;

                const moveToModuleEntry = err.response.data.moveToModuleEntry;

                context.changeModuleContext(moveToModuleEntry, redirectURL);
              } else {
                context.dispatchError({errorMessage: err.response.data.error, redirectURL: '/problems'});
              }
            } else {
              context.dispatchError({
                errorMessage: 'There was an error. Please try again later',
                redirectURL: '/problems',
              });
            }
          }
        },
      });
    }
  }, [context.user, groupContestId, update]);

  const tabs = useMemo(
    () =>
      getTabs(
        context.user?.role === Role.ADMIN ||
          context.user?.activeModule?.role === ModuleRoleCode.TEACHER ||
          context.user?.activeModule?.role === ModuleRoleCode.AUTHOR_TEACHER ||
          context.user?.activeModule?.role === ModuleRoleCode.COURSE_ADMIN
      ),
    [context.user?.role, context.user?.activeModule]
  );

  return (
    <Page>
      {contest || isLoading ? (
        <div className="flex items-center">
          <div className="grid grid-cols-12 item-center justify-center rounded-2xl bg-card-main mb-10 min-w-[90vw]">
            <div className="col-span-12 min-h-[85vh] min-w-[90vw]">
              <div className="min-h-[83vh]">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-start">
                    <IconButton onClick={() => navigate('/contests')} className="text-white">
                      {' '}
                      <ArrowBackIosNew sx={{fontSize: 54}} className=" text-white" />{' '}
                    </IconButton>
                    <div className="flex justify-start flex-col ml-3">
                      {contest && <Typography variant="h3">{contest.title}</Typography>}
                      {isLoading && <Skeleton variant="text" width={760} height={37.35} />}
                      <div className="flex">
                        {contest && (
                          <Typography variant="h5_italic">
                            {stringInterval(new Date(contest.startDate), new Date(contest.endDate))}
                          </Typography>
                        )}
                        {isLoading && <Skeleton variant="text" width={580} height={30} />}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-row justify-evenly gap-1 py-3 px-1 text-center h-[100%] select-none bg-primary-main">
                  {tabs.map((tab) => (
                    <Link key={`${tab.name}`} to={tab.path} replace={true} className="no-underline">
                      <Typography
                        style={{
                          color: location.pathname.includes(tab.path) ? '#ffffff' : '#C5C5C5',
                        }}
                        variant="h6"
                      >
                        {typeof tab.name === 'string' ? tab.name.toUpperCase() : tab.name}
                      </Typography>
                    </Link>
                  ))}
                </div>

                <div className="p-5">
                  {contest && <Outlet context={{contest, isOwner: isPrivileged, groupContestId}} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </Page>
  );
};

const getTabs = (isTeacher: boolean) => {
  const commonTabs = [
    {
      name: 'Problems',
      path: 'problems',
    },
    {
      name: 'Details',
      path: 'details',
    },
    {
      name: 'Leaderboard',
      path: 'leaderboard',
    },
  ];

  if (isTeacher) {
    commonTabs.push({
      name: 'Statistics',
      path: 'statistics',
    });
    commonTabs.push({
      name: 'Submissions',
      path: 'submissions',
    });
  }

  if (!isTeacher) {
    commonTabs.push({
      name: 'My Submissions',
      path: 'my-submissions',
    });
  }
  return commonTabs;
};
